import { forwardRef, useImperativeHandle, useMemo, useRef, type ForwardedRef } from "react";
import { Table as ZhTable, type TableProps, type TableRef } from "zhihao-ui-react";
import type { ColumnProps } from "@/views/components/ProTable/interface";
import { toReactTableColumn } from "./components/TableColumn";
import { useViewportTableMaxHeight } from "@/views/components/DetailTable/useViewportTableMaxHeight";

export interface ProTableProps<RecordType extends Record<string, any> = Record<string, any>> extends Omit<
    TableProps<RecordType>,
    | "columns"
    | "data"
    | "requestApi"
    | "requestAuto"
    | "requestError"
    | "handleParam"
    | "dataCallback"
    | "initParam"
    | "pagination"
    | "rowKey"
> {
    columns: ColumnProps<RecordType>[];
    data?: RecordType[];
    requestApi?: (params: any) => Promise<any>;
    requestAuto?: boolean;
    requestError?: (params: any) => void;
    handleParam?: (data: any) => any;
    dataCallback?: (data: any) => any;
    selectChangeCallback?: (data: RecordType[]) => any;
    title?: string;
    pagination?: boolean;
    initParam?: any;
    rowKey?: string;
    customColumnStorageKey?: string;
    offset?: number;
}

export type ProTableRef<RecordType extends Record<string, any> = Record<string, any>> = TableRef<RecordType>;

/** Vue ProTable 的 React 适配入口，底层直接复用 zhihao-ui-react Table。 */
const ProTable = forwardRef(function ProTable<RecordType extends Record<string, any>>(
    props: ProTableProps<RecordType>,
    ref: ForwardedRef<ProTableRef<RecordType>>
) {
    const tableRef = useRef<TableRef<RecordType>>(null);
    const { columns, title, customColumnStorageKey, selectChangeCallback, rowKey = "id", ...tableProps } = props;
    // 旧版 ProTable 会根据表格顶部实时计算最大高度，确保大页码列表仍可看到分页。
    const { hostRef, maxHeight: viewportMaxHeight } = useViewportTableMaxHeight(
        tableProps.pagination !== false && tableProps.height == null && tableProps.maxHeight == null,
        tableProps.data?.length
    );
    const reactColumns = useMemo(
        () =>
            columns
                .filter(column => column.isShow !== false)
                .map(column => toReactTableColumn(column, new Map(), tableProps.data)),
        [columns, tableProps.data]
    );

    useImperativeHandle(ref, () => tableRef.current as ProTableRef<RecordType>, []);

    return (
        <div ref={hostRef} className="no-card table-main">
            {title ? <div className="table-title">{title}</div> : null}
            <ZhTable<RecordType>
                {...tableProps}
                maxHeight={tableProps.maxHeight ?? viewportMaxHeight}
                ref={tableRef}
                columns={reactColumns}
                rowKey={rowKey}
                customColumn={Boolean(customColumnStorageKey)}
                customColumnCacheKey={customColumnStorageKey}
                onSelectionChange={selectChangeCallback}
            />
        </div>
    );
});

export { ProTable };
export default ProTable;
